import React from "react";
import Box from "@vapor/react-material/Box";
import Select from "@vapor/react-material/Select";
import Button from "@vapor/react-material/Button";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import { ListItemProps } from "@vapor/react-material/ListItem";
import ListItemSecondaryAction from "@mui/material/ListItemSecondaryAction";
import { SelectProps } from "@vapor/react-material/Select";
import Autocomplete from "@vapor/react-material/Autocomplete";
import Stack from "@vapor/react-material/Stack";
import TextField from "@vapor/react-material/TextField";
import Search from "@mui/icons-material/Search";
import Delete from "@mui/icons-material/Delete";
import Edit from "@mui/icons-material/Edit";
import ListItem from "@vapor/react-material/ListItem";
import ListItemText from "@vapor/react-material/ListItemText";
import Typography from "@vapor/react-extended/ExtendedTypography";
import MenuItem from "@vapor/react-material/MenuItem";
import IconButton from "@vapor/react-material/IconButton";
import InputLabel from "@vapor/react-material/InputLabel";
import {
  StyledAutocompletePopper,
  StyledPopper
} from "./StyledAutocompletePopper";
import { StyledAutocompletePopperFreeText } from "./StyledAutocompletePopperFreeText";
import { ToolbarBottom } from "./ToolbarBottom";

const warnFreeText = () => {
  console.warn(
    [
      "`freeText` prop is no longer supported in VaporLookup component => @vapor/react-custom. Use `freeSolo` instead.",
      "For more Info stay updated (https://biosphere.teamsystem.com/vapor/react/vapor-custom/-/blob/develop/CHANGELOG.md)"
    ].join("\n")
  );
};

const warnGetValue = () => {
  console.warn(
    [
      "`getValue` prop is no longer supported in VaporLookup component => @vapor/react-custom. Use `onSelected` instead.",
      "For more Info stay updated (https://biosphere.teamsystem.com/vapor/react/vapor-custom/-/blob/develop/CHANGELOG.md)"
    ].join("\n")
  );
};

interface OnChangeHandler {
  (e): void;
}

export type VaporLookupProps = {
  id?: string;
  label?: string;
  options?: ItemProps[];
  onNewValue?: () => void;
  value?: "" | any;
  secondaryitem?: string;
  onEditItem?: (itemEdit: any) => {};
  onDeleteItem?: (itemDelete: any) => {};
  freeSolo?: boolean;
  onSearchChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  sx?: any;
  disabled?: boolean;
  open?: boolean;
  onSelected?: (item: any) => {};
  placement?:
    | "auto-end"
    | "auto-start"
    | "auto"
    | "bottom-end"
    | "bottom-start"
    | "bottom"
    | "left-end"
    | "left-start"
    | "left"
    | "right-end"
    | "right-start"
    | "right"
    | "top-end"
    | "top-start"
    | "top";
  widthPopper?: any;
  freeText?: boolean;
  getValue?: Function;
  placeholder?: string;
} & SelectProps;

export type ItemProps = {
  id: string | number;
  value: any;
  label?: string;
  secondary?: string;
} & ListItemProps;

export const VaporLookup = ({ ...props }: VaporLookupProps) => {
  const {
    onNewValue,
    onEditItem,
    label,
    id,
    placement = "bottom-start",
    sx,
    open = false,
    disabled = false,
    value,
    options,
    onSelected,
    widthPopper,
    onSearchChange,
    freeText,
    getValue,
    onDeleteItem,
    placeholder = "Search"
  } = props;

  const getOptionSelected = (id, allOptions) => {
    const resultSelced = allOptions.find((item) => item.id === id);
    return resultSelced;
  };

  // const [selected, setSelected] = React.useState(
  //   value
  //     ? typeof value === "object"
  //       ? getOptionSelected(value.id, options)
  //       : value
  //     : props.freeSolo
  //     ? ""
  //     : null
  // );

  const [newValue, setNewValue] = React.useState("");
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [openPopper, setOpenPopper] = React.useState(open);
  const [openFreeSolo, setOpenFreeSolo] = React.useState(open);
  const [freeSolo, setFreeSolo] = React.useState(false);

  const refSelect = React.useRef(null);
  const refPopperFree = React.useRef(null);

  const [widthSelect, setWidth] = React.useState(0);

  React.useLayoutEffect(() => {
    setWidth(widthPopper ? widthPopper : refSelect?.current?.offsetWidth);
  }, [refSelect?.current?.offsetWidth]);

  React.useEffect(() => {
    // warning props
    freeText && warnFreeText();
    getValue && warnGetValue();
    // console.log("Height: ", refPopperFree?.current?.clientHeight);
    setFreeSolo(props.freeSolo ? props.freeSolo : freeText ? freeText : false); // freeText deprecated
  }, []);

  const selectStyle = {
    width: "100%",
    "&.MuiTextField-root": {
      paddingLeft: 0,
      paddingRight: 0
    },
    "& fieldset": { top: 0 },
    "& legend": {
      display: "none"
    },
    "& .MuiOutlinedInput-notchedOutline": {
      top: 0
    }
  };

  const addNew = () => {
    setOpenPopper(false);
    onNewValue();
  };

  const handleOpen = (event) => {
    if (!disabled) {
      setAnchorEl(event.currentTarget);
      setOpenPopper(!openPopper);
    }
  };

  const handleOpenFreeSolo = (event) => {
    if (!disabled) {
      setAnchorEl(event.currentTarget);
      setOpenFreeSolo(!openFreeSolo);
    }
  };

  const handleClose = () => {
    setOpenPopper(false);
    setOpenFreeSolo(false);
  };

  const handleSelectItem = (item: any) => {
    // setSelected(item);
    onSelected(item);
    getValue && getValue(item); // deprecated
    handleClose();
  };

  const handleEditItem = (item: any) => {
    onEditItem(item);
  };

  const handleDeleteItem = (item: any) => {
    onDeleteItem(item);
  };

  const getLabelList = (itemList) => {
    if (typeof itemList === "object") {
      return itemList?.label ? itemList?.label : itemList?.value;
    } else {
      return itemList;
    }
  };

  return (
    <Box sx={{ width: "100%", ...sx }}>
      <InputLabel id={id} disabled={disabled}>
        {label}
      </InputLabel>
      <ClickAwayListener onClickAway={handleClose}>
        <div>
          {!freeSolo && (
            <Select
              onOpen={handleOpen}
              open={false}
              sx={{
                color: (e) => {
                  return (
                    disabled && `${e.palette.primary["textDisabledColor"]}`
                  );
                },
                ...selectStyle
              }}
              value={value}
              disabled={disabled}
              // options={options}
              // placement={placement}
              ref={refSelect}
              onChange={props.onChange}
            >
              {options.map((option: ItemProps | string, index: number) => (
                <MenuItem
                  key={index}
                  value={typeof option === "object" ? option.value : option}
                >
                  {getLabelList(option)}
                </MenuItem>
              ))}
            </Select>
          )}
          {freeSolo && (
            <Autocomplete
              sx={{
                "&.MuiAutocomplete-root-Autocomplete-root .MuiOutlinedInput-root":
                  {
                    padding: "0px 6px 0px 6px!important"
                  },
                // ...sx,
                width: sx?.width
              }}
              options={options}
              freeSolo={true}
              open={openFreeSolo}
              disableClearable
              value={value}
              onOpen={handleOpenFreeSolo}
              disabled={disabled}
              PopperComponent={(props) => (
                <div ref={refPopperFree}>
                  <StyledPopper
                    sx={{
                      maxHeight: "544px",
                      height: onNewValue ? "380px" : "330px",
                      minHeight: "300px",
                      width: widthPopper && `${widthPopper} !important`
                      // "& .MuiPopper-root-Popper-root-StyledAutocompletePopperFreeText-root-MuiAutocomplete-popper .MuiAutocomplete-paper":{
                      //   width: widthPopper && `${widthSelect} !important`
                      // }
                    }}
                    placement={placement}
                    anchorEl={props.anchorEl}
                    open={openFreeSolo}
                    // onClickAway={handleClose}
                    {...props}
                    //ref={refPopperFree}
                    width={widthPopper && widthPopper}
                    modifiers={[
                      {
                        name: "flip",
                        enabled: false,
                        options: {
                          altBoundary: true,
                          rootBoundary: "document",
                          padding: 8
                        }
                      }
                    ]}
                  >
                    <Box>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          // paddingTop: "20px",
                          // paddingBottom: "20px",
                          alignItems: "center",
                          flexGrow: 1
                        }}
                      >
                        <StyledAutocompletePopperFreeText
                          placement={placement}
                          anchorEl={props.anchorEl}
                          {...props}
                          width={widthPopper && widthPopper}
                        />
                      </Box>
                      {onNewValue && (
                        <ToolbarBottom label={label} addNew={addNew} />
                      )}
                    </Box>
                  </StyledPopper>
                </div>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={placeholder}
                  onChange={onSearchChange}
                  type="search"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      padding: "0px 3px 0px 0px!important"
                    }
                  }}
                />
              )}
              renderOption={(
                props,
                option: ItemProps | string,
                { selected }
              ) => {
                return (
                  <ListItem
                    {...props}
                    key={typeof option === "object" ? option.id : option}
                    selected={selected}
                    onClick={() => handleSelectItem(option)}
                    sx={{
                      paddingLeft: 0,
                      paddingRight: 0,
                      "&.MuiTypography-root.MuiListItemText-primary": {
                        paddingRight: "60x"
                      }
                    }}
                    alignItems="flex-start"
                  >
                    <ListItemText
                      sx={{
                        paddingRight: onEditItem && onDeleteItem && 7
                      }}
                      key={typeof option === "object" ? option.id : option}
                      primary={
                        <Typography
                          sx={{ display: "inline" }}
                          component="span"
                          variant="body"
                          color="primary.main"
                        >
                          {getLabelList(option)}
                        </Typography>
                      }
                      secondary={
                        typeof option === "object" &&
                        option.secondary && (
                          <React.Fragment>
                            <Typography
                              sx={{ display: "inline" }}
                              component="span"
                              variant="body"
                              color="primary.textSubduedColor"
                            >
                              {option.secondary ? option.secondary : " "}
                            </Typography>
                          </React.Fragment>
                        )
                      }
                    />
                    {(onEditItem || onDeleteItem) && (
                      <ListItemSecondaryAction>
                        {onEditItem && (
                          <IconButton
                            //edge="end"
                            onClick={() => handleEditItem(option)}
                            aria-label="edit"
                            sx={{
                              color: (e) => {
                                return `${e.palette?.primary["iconDefault"]}`;
                              },
                              ...selectStyle
                            }}
                          >
                            <Edit />
                          </IconButton>
                        )}
                        {onDeleteItem && (
                          <IconButton
                            //edge="end"
                            onClick={() => handleDeleteItem(option)}
                            aria-label="delete"
                            sx={{
                              color: (e) => {
                                return `${e.palette?.primary["iconDefault"]}`;
                              },
                              ...selectStyle
                            }}
                          >
                            <Delete />
                          </IconButton>
                        )}
                      </ListItemSecondaryAction>
                    )}
                  </ListItem>
                );
              }}
            />
          )}

          {/* Popper for Search list */}
          <StyledPopper
            sx={{
              maxHeight: "444px",
              height: onNewValue ? "380px" : "330px",
              width: widthSelect
            }}
            placement={placement}
            anchorEl={anchorEl}
            open={openPopper}
          >
            <Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  paddingTop: "20px",
                  paddingBottom: "20px",
                  paddingLeft: "16px",
                  paddingRight: "16px",
                  alignItems: "center",
                  direction: "row",
                  // mb: "56px",
                  flexGrow: 1
                }}
              >
                <Stack direction="row" sx={{ width: "100%" }}>
                  <Autocomplete
                    sx={{ flexGrow: 1 }}
                    options={options}
                    freeSolo
                    disableClearable
                    PopperComponent={(props) => (
                      <StyledAutocompletePopper
                        {...props}
                        width={widthSelect}
                      />
                    )}
                    open={true}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder={placeholder}
                        onChange={onSearchChange}
                        type="search"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            padding: "0px 6px 0px 6px!important"
                          }
                        }}
                      />
                    )}
                    renderOption={(
                      props,
                      option: ItemProps | string,
                      { selected }
                    ) => {
                      return (
                        <ListItem
                          {...props}
                          key={typeof option === "object" ? option?.id : option}
                          selected={selected}
                          onClick={() => handleSelectItem(option)}
                          sx={{
                            paddingLeft: 0,
                            paddingRight: 0,
                            width: widthSelect
                            // ...props.sx
                          }}
                          alignItems="flex-start"
                        >
                          <ListItemText
                            sx={{
                              paddingRight: onEditItem && onDeleteItem && 7
                            }}
                            key={
                              typeof option === "object" ? option?.id : option
                            }
                            primary={
                              <Typography
                                sx={{ display: "inline", paddingRight: "20x" }}
                                component="span"
                                variant="body"
                                color="primary.main"
                              >
                                {getLabelList(option)}
                              </Typography>
                            }
                            secondary={
                              typeof option === "object" &&
                              option?.secondary && (
                                <React.Fragment>
                                  <Typography
                                    sx={{
                                      display: "inline",
                                      paddingRight: "20x"
                                    }}
                                    component="span"
                                    variant="body"
                                    color="primary.textSubduedColor"
                                  >
                                    {option.secondary ? option.secondary : " "}
                                  </Typography>
                                </React.Fragment>
                              )
                            }
                          />
                          {(onEditItem || onDeleteItem) && (
                            <ListItemSecondaryAction>
                              {onEditItem && (
                                <IconButton
                                  //edge="end"
                                  onClick={() => handleEditItem(option)}
                                  aria-label="edit"
                                  sx={{
                                    color: (e) => {
                                      return `${e.palette?.primary["iconDefault"]}`;
                                    },
                                    ...selectStyle
                                  }}
                                >
                                  <Edit />
                                </IconButton>
                              )}
                              {onDeleteItem && (
                                <IconButton
                                  //edge="end"
                                  onClick={() => handleDeleteItem(option)}
                                  aria-label="delete"
                                  sx={{
                                    color: (e) => {
                                      return `${e.palette?.primary["iconDefault"]}`;
                                    },
                                    ...selectStyle
                                  }}
                                >
                                  <Delete />
                                </IconButton>
                              )}
                            </ListItemSecondaryAction>
                          )}
                        </ListItem>
                      );
                    }}
                  />
                  <Button
                    size="medium"
                    variant="contained"
                    sx={{
                      "&.MuiButtonBase-root.MuiButton-root.MuiButton-sizeMedium":
                        {
                          minWidth: 40,
                          p: 1,
                          borderRadius: 0
                        }
                    }}
                  >
                    <Search />
                  </Button>
                </Stack>
              </Box>
            </Box>
            {onNewValue && <ToolbarBottom label={label} addNew={addNew} />}
          </StyledPopper>
        </div>
      </ClickAwayListener>
    </Box>
  );
};
